import React from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface ImportTemplatesLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetFolderName: string;
  folderImportAvailable: boolean;
  lanDevImport?: boolean;
  onPickImportSource: () => void;
  importing?: boolean;
}

const ImportTemplatesLibraryModal: React.FC<ImportTemplatesLibraryModalProps> = ({
  isOpen,
  onClose,
  targetFolderName,
  folderImportAvailable,
  lanDevImport = false,
  onPickImportSource,
  importing = false,
}) => {
  const { colors } = useTheme();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader>
        <ModalTitle>Import into library</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          disabled={importing}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover, importing && 'opacity-50')}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        <p
          className={cn(
            'mb-3 text-sm rounded-lg border px-3 py-2 font-medium',
            'border-violet-200 bg-violet-50 text-violet-900 dark:border-violet-800/50 dark:bg-violet-950/40 dark:text-violet-100',
          )}
        >
          Destination: {targetFolderName}
        </p>
        <p className={cn('text-sm', colors.textSecondary)}>
          Choose a folder on your computer, or drag a folder onto the library page. Files go into the
          selected library folder. Nested subfolders inside your import are created under it. Templates
          (.eml, .msg, .html, …) and documents (.pdf, .doc, .xlsx, …) are supported.
        </p>
        {lanDevImport && (
          <p className={cn('mt-3 text-sm rounded-lg border px-3 py-2', colors.border, colors.surfaceHover, colors.textSecondary)}>
            LAN dev over HTTP: Chrome may show its own folder confirmation first; you will then see this app&apos;s
            upload confirmation before files are sent.
          </p>
        )}
        {!folderImportAvailable && (
          <p className={cn('mt-3 text-sm rounded-lg border px-3 py-2', colors.border, colors.surfaceHover, colors.textSecondary)}>
            Folder import is not available on plain HTTP over a LAN IP in production builds. Use HTTPS or{' '}
            <code className="text-xs">http://localhost</code> instead.
          </p>
        )}
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start"
            disabled={importing || !folderImportAvailable}
            onClick={onPickImportSource}
          >
            Choose folder on this computer…
          </Button>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={importing}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ImportTemplatesLibraryModal;
